import { useState } from "react";
import { Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { diaDaSemana, estiloStatusTurno, formatBRL, formatarData, periodoDoTurno } from "@/lib/utils";
import { ModalCriarTurno } from "./ModalCriarTurno";
import type { TurnoCalendario } from "@/types";

export function ModalTurnosDoDia({
  dataIso,
  turnos,
  open,
  onOpenChange,
  onChanged,
}: {
  dataIso: string;
  turnos: TurnoCalendario[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}) {
  const [adicionando, setAdicionando] = useState(false);
  const excluir = trpc.turnos.excluir.useMutation({ onSuccess: onChanged });

  return (
    <>
      <Dialog open={open && !adicionando} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Turnos — {formatarData(dataIso)}</DialogTitle>
            <DialogDescription>
              {diaDaSemana(dataIso, true)} · Visualize ou adicione turnos para este dia.
            </DialogDescription>
          </DialogHeader>

          <div className="-mx-6 max-h-96 overflow-y-auto px-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Turnos agendados
            </div>
            <ul className="mt-2 space-y-2">
              {turnos.length === 0 && (
                <li className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
                  Nenhum turno neste dia.
                </li>
              )}
              {turnos.map((t) => {
                const per = periodoDoTurno(t.horaInicio, t.horaFim);
                return (
                  <li
                    key={t.id}
                    className="flex items-start justify-between gap-3 rounded-md border border-slate-200 p-3"
                  >
                    <div className="flex flex-1 items-start gap-3">
                      <span
                        className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: t.tipoTurnoCor }}
                      />
                      <div className="space-y-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">{t.profissionalNome}</span>
                          <span
                            className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ring-1 ring-inset ${per.classe}`}
                            title={`${t.horaInicio}–${t.horaFim}`}
                          >
                            {per.label}
                          </span>
                          {/* F2: o que a conferencia mensal decidiu deste turno. */}
                          {estiloStatusTurno(t.status) && (
                            <span
                              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${estiloStatusTurno(t.status)!.classe}`}
                            >
                              {estiloStatusTurno(t.status)!.rotulo}
                            </span>
                          )}
                          {t.acionado && (
                            <span className="inline-flex items-center rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 ring-1 ring-inset ring-violet-200">
                              sobreaviso acionado
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600">{t.tipoTurnoNome}</div>
                        <div className="text-xs text-slate-400">
                          {t.horaInicio}–{t.horaFim} · {formatBRL(t.tipoTurnoValor)}
                        </div>
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Excluir turno"
                      onClick={() => {
                        if (confirm(`Excluir turno de ${t.profissionalNome}?`)) {
                          excluir.mutate({ id: t.id });
                        }
                      }}
                    >
                      <X className="h-4 w-4 text-slate-500" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <Button onClick={() => setAdicionando(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Adicionar novo turno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {adicionando && (
        <ModalCriarTurno
          dataIso={dataIso}
          open
          onOpenChange={(o) => !o && setAdicionando(false)}
          onCreated={() => {
            setAdicionando(false);
            onChanged();
          }}
        />
      )}
    </>
  );
}
